<script setup>
import { ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import dealReportsAPI from 'dashboard/api/dealReports';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import { useLocalizedTime } from 'shared/composables/useLocalizedTime';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import { formatCurrencyBRL } from './commercialFormatters';

const props = defineProps({
  params: { type: Object, required: true },
});

const REFRESH_INTERVAL = 30000;

const EVENT_STYLES = {
  won: { icon: 'i-lucide-trophy', color: 'bg-n-teal-3 text-n-teal-11' },
  lost: { icon: 'i-lucide-circle-x', color: 'bg-n-ruby-3 text-n-ruby-11' },
  moved: { icon: 'i-lucide-arrow-right', color: 'bg-n-iris-3 text-n-iris-11' },
  created: { icon: 'i-lucide-plus', color: 'bg-n-blue-3 text-n-blue-11' },
};

const { t } = useI18n();
const { relativeTime } = useLocalizedTime();
const events = ref([]);

// Falha silenciosa: o mural mantém o que já carregou.
const fetchData = async () => {
  try {
    const response = await dealReportsAPI.getActivity(props.params);
    events.value = response.data.events;
  } catch {
    // mantém os últimos eventos
  }
};

const { startRefetching } = useLiveRefresh(fetchData, REFRESH_INTERVAL);

watch(() => props.params, fetchData, { deep: true });
onMounted(() => {
  fetchData();
  startRefetching();
});

const userName = event =>
  event.user?.name || t('DASHBOARD.COMERCIAL.ACTIVITY.SYSTEM');
</script>

<template>
  <div class="flex flex-col h-full gap-3 p-4 border rounded-lg border-n-weak">
    <h3 class="flex items-center gap-2 font-medium text-n-slate-12">
      <span class="relative flex size-2">
        <span
          class="absolute inline-flex w-full h-full rounded-full opacity-75 animate-ping bg-n-teal-9"
        />
        <span class="relative inline-flex rounded-full size-2 bg-n-teal-9" />
      </span>
      {{ t('DASHBOARD.COMERCIAL.ACTIVITY.TITLE') }}
    </h3>
    <p v-if="!events.length" class="text-sm text-n-slate-10">
      {{ t('DASHBOARD.COMERCIAL.ACTIVITY.EMPTY') }}
    </p>
    <ul v-else class="flex flex-col gap-3 overflow-y-auto max-h-80">
      <li v-for="event in events" :key="event.id" class="flex gap-3">
        <span
          class="grid rounded-full size-8 shrink-0 place-items-center"
          :class="EVENT_STYLES[event.type].color"
        >
          <Icon :icon="EVENT_STYLES[event.type].icon" class="size-4" />
        </span>
        <div class="flex flex-col min-w-0 gap-0.5">
          <p class="m-0 text-sm text-n-slate-12">
            <strong>{{ userName(event) }}</strong>
            {{ t(`DASHBOARD.COMERCIAL.ACTIVITY.${event.type.toUpperCase()}`) }}
            <strong>{{ event.deal.name }}</strong>
            <span
              v-if="event.type === 'won' && event.deal.value"
              class="font-medium text-n-teal-11"
            >
              · {{ formatCurrencyBRL(event.deal.value) }}
            </span>
          </p>
          <p
            v-if="event.type === 'moved'"
            class="flex items-center gap-1 m-0 text-xs text-n-slate-11"
          >
            {{ event.from_stage }}
            <Icon icon="i-lucide-arrow-right" class="size-3" />
            {{ event.to_stage }}
          </p>
          <span class="text-xs text-n-slate-10">
            {{ relativeTime(event.at) }}
          </span>
        </div>
        <Avatar
          v-if="event.user"
          class="ltr:ml-auto rtl:mr-auto"
          :src="event.user.avatar_url"
          :name="event.user.name"
          :size="24"
          rounded-full
        />
      </li>
    </ul>
  </div>
</template>
